import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/media/hero-poster`;
const HERO_VIDEO = `${process.env.REACT_APP_BACKEND_URL}/api/media/hero-video`;

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
    <motion.span
      className="block pr-[0.08em]"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.3, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const [verse, setVerse] = useState({
    texto: "El viento sopla de donde quiere, y oyes su sonido; mas ni sabes de dónde viene, ni a dónde va; así es todo aquel que es nacido del Espíritu.",
    referencia: "Juan 3:8",
  });

  useEffect(() => {
    axios.get(`${API}/verse`).then(({ data }) => setVerse(data)).catch(() => {});
  }, []);

  // Autoplay robusto en móvil: reintenta al cargar y en el primer toque del usuario
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    v.addEventListener("canplay", tryPlay);
    window.addEventListener("touchstart", tryPlay, { once: true });
    window.addEventListener("click", tryPlay, { once: true });
    return () => {
      v.removeEventListener("canplay", tryPlay);
      window.removeEventListener("touchstart", tryPlay);
      window.removeEventListener("click", tryPlay);
    };
  }, []);

  return (
    <section id="inicio" ref={ref} className="relative flex min-h-screen items-end overflow-hidden bg-ink" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: 1.15 }}>
        <img src={HERO_IMG} alt="Textura etérea de viento y luz en la oscuridad" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={HERO_IMG}
          data-testid="hero-video"
        >
          <source src={`${process.env.REACT_APP_BACKEND_URL}/api/media/hero-webm`} type="video/webm" />
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/70" />
      </motion.div>

      <span
        className="vertical-label absolute right-6 top-1/2 hidden -translate-y-1/2 text-[10px] font-light uppercase tracking-[0.5em] text-paper/40 lg:block"
        data-testid="hero-vertical-label"
      >
        Iglesia Cristiana — Toluca, México
      </span>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pb-24 lg:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="mb-8 flex items-center gap-4 text-[10px] font-light uppercase tracking-[0.5em] text-fire"
          data-testid="hero-eyebrow"
        >
          <motion.span
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="inline-block h-px w-20 origin-left bg-fire"
          />
          Juan 3:8
        </motion.p>

        <h1 className="font-serif uppercase leading-[0.88] tracking-tight text-paper" data-testid="hero-title">
          <Line delay={0.7}>
            <span className="block text-[17vw] lg:text-[13vw]">Viento</span>
          </Line>
          <Line delay={0.95}>
            <span className="block text-[17vw] lg:text-[13vw]">
              de <em className="text-gold">Gracia</em>
            </span>
          </Line>
        </h1>

        <div className="mt-10 flex flex-col justify-between gap-10 border-t border-white/10 pt-8 md:flex-row md:items-end">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="max-w-md text-sm font-normal leading-relaxed tracking-wide text-paper/75"
            data-testid="hero-verse"
          >
            «{verse.texto}»
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.7, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap items-center gap-4"
          >
            <a
              href="#ubicacion"
              onClick={(e) => { e.preventDefault(); window.__lenis?.scrollTo("#ubicacion", { offset: -60 }); }}
              data-testid="hero-visit-btn"
              className="bg-paper px-8 py-4 text-[11px] font-medium uppercase tracking-[0.3em] text-ink transition-colors duration-300 hover:bg-fire hover:text-paper"
            >
              Visítanos — Dom 10 AM
            </a>
            <a
              href="#contacto"
              onClick={(e) => { e.preventDefault(); window.__lenis?.scrollTo("#contacto", { offset: -60 }); }}
              data-testid="hero-prayer-btn"
              className="border border-paper/30 px-8 py-4 text-[11px] font-light uppercase tracking-[0.3em] text-paper transition-colors duration-300 hover:border-paper hover:bg-paper hover:text-ink"
            >
              Petición de oración
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-0 left-1/2 z-10 hidden h-20 w-px md:block"
        data-testid="hero-scroll-indicator"
      >
        <motion.span
          className="block h-full w-full origin-top bg-gradient-to-b from-transparent via-paper/60 to-fire"
          animate={{ scaleY: [0, 1, 1, 0], opacity: [0, 1, 1, 0] }}
          transition={{ repeat: Infinity, duration: 2.6, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
};

export default Hero;
